import React from 'react';
import { Box, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useField } from 'formik';
import moment from 'moment';
import CustomInputField from './CustomInputField';
import CustomValidationText from '../CustomValidationText';
import CustomLabel from '../CustomLabel';
import { useIsRequired } from './RequiredFieldsContext';

const CustomDatePicker = ({
    name,
    label,
    placeholder,
    minDate,
    maxDate,
    fullWidth = true,
    sx,
    labelStyle = {},
    InputProps = {},
    required = false,
    ...props
}) => {
    const [field, meta, helpers] = useField(name);
    // Fall back to the Yup schema so required fields are marked automatically.
    const autoRequired = useIsRequired(name);
    const isRequired = required || autoRequired;

    const handleDateChange = (newValue) => {
        if (newValue) {
            helpers.setValue(newValue.toISOString());
        } else {
            helpers.setValue('');
        }
    };

    const configDatePicker = {
        ...props,
        sx: {
            width: "100%",
            background: '#fff',
            marginBottom: 0,
            '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E4E7EC' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E4E7EC' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#101828',
                    borderWidth: '1px',
                },
            },
            '& .MuiOutlinedInput-input': {
                padding: '15px 16px',
                height: 'auto',
                fontSize: '1rem',
                boxSizing: 'border-box',
            },
        },
        value: field.value ? moment(field.value) : null, // Ensure the value is a moment object
        onChange: handleDateChange,
        minDate: minDate ? moment(minDate) : null, // Parse minDate to moment object if it exists
        maxDate: maxDate ? moment(maxDate) : null, // Parse maxDate to moment object if it exists
        renderInput: (params) => (
            <CustomInputField
                {...params}
                fullWidth={fullWidth}
                name={name}
                sx={{ padding: '9px 14px',...sx }}
                InputProps={InputProps}
            />
        ),
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ width: fullWidth ? '100%' : sx.width }}>
                {label && (
                    <CustomLabel required={isRequired} sx={{ ...labelStyle }}>
                        {label}
                    </CustomLabel>
                )}
                <DatePicker
                    {...configDatePicker}
                />
                {meta && meta.touched && meta.error && (
                    <CustomValidationText value={meta.error} />
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default CustomDatePicker;
